import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@astro/auth'
import { apiClient, ApiRequestError } from '@astro/api'
import { otpRequestSchema, otpVerifySchema } from '@astro/validators'
import { useToast } from '../../context/ToastContext'
import { WhatsAppIcon } from './AuthControls'
import CountryCodeSelect from './CountryCodeSelect'
import { toFieldErrors } from './formErrors'
import {
  DEFAULT_COUNTRY,
  DEFAULT_COUNTRY_ISO2,
  getCountryByIso2,
  isValidNationalNumber,
  toE164,
} from '../../data/countries'

const OTP_LENGTH = 4
const OTP_RESEND_SECONDS = 30

/** WhatsApp OTP sign-in — step 'mobile' collects the number, 'verify' the code. */
export default function OtpLoginForm({ onBackToLogin }) {
  const { showToast } = useToast()
  const { loginWithOtp } = useAuth()

  const [step, setStep] = useState('mobile')
  const [countryIso2, setCountryIso2] = useState(DEFAULT_COUNTRY_ISO2)
  const [mobile, setMobile] = useState('')
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''))
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const inputsRef = useRef([])

  const country = getCountryByIso2(countryIso2) ?? DEFAULT_COUNTRY
  const { min: minDigits, max: maxDigits } = country.phoneLength
  const phonePlaceholder =
    minDigits === maxDigits ? `${maxDigits}-digit mobile number` : `${minDigits}–${maxDigits} digit mobile number`

  // Upstream's `mobile_no` has always been a bare 10-digit Indian number — keep
  // that exact shape for India, and send E.164 elsewhere so the dial code travels.
  const mobileNo = country.iso2 === DEFAULT_COUNTRY_ISO2 ? mobile : toE164(country.iso2, mobile)

  /** Re-clamp the entered digits when the country's number length shrinks. */
  const handleCountryChange = (iso2) => {
    setCountryIso2(iso2)
    setMobile((prev) => prev.slice(0, (getCountryByIso2(iso2) ?? DEFAULT_COUNTRY).phoneLength.max))
  }

  /** The shared OTP schemas are India-only — validate other countries by their own length. */
  const withCountryMobileRule = (fieldErrors) => {
    if (country.iso2 === DEFAULT_COUNTRY_ISO2) return fieldErrors
    if (isValidNationalNumber(country.iso2, mobile)) delete fieldErrors.mobile
    else fieldErrors.mobile = `Enter a valid ${country.name} mobile number`
    return fieldErrors
  }

  // Resend cooldown ticker — re-arms itself each second while > 0.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  // Autofocus the first OTP box as soon as the code-entry step appears.
  useEffect(() => {
    if (step === 'verify') inputsRef.current[0]?.focus()
  }, [step])

  const handleSendOtp = async (event) => {
    event.preventDefault()
    const parsed = otpRequestSchema.safeParse({ mobile })
    const fieldErrors = withCountryMobileRule(parsed.success ? {} : toFieldErrors(parsed.error.issues))
    if (Object.keys(fieldErrors).length) return setErrors(fieldErrors)

    setErrors({})
    setSubmitting(true)
    try {
      // Upstream's field is `mobile_no`, not `mobile`.
      const res = await apiClient.post('/login/otp/request', { mobile_no: mobileNo })
      setCode(Array(OTP_LENGTH).fill(''))
      setStep('verify')
      setResendCooldown(OTP_RESEND_SECONDS)
      showToast(res?.message || 'OTP sent successfully via WhatsApp.')
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setErrors(toFieldErrors(err.error.details))
        showToast(err.error.message || 'Could not send OTP. Please try again.')
      } else {
        showToast('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || submitting) return
    setSubmitting(true)
    try {
      const res = await apiClient.post('/login/otp/resend', { mobile_no: mobileNo })
      setCode(Array(OTP_LENGTH).fill(''))
      setResendCooldown(OTP_RESEND_SECONDS)
      showToast(res?.message || 'OTP resent via WhatsApp.')
      inputsRef.current[0]?.focus()
    } catch (err) {
      if (err instanceof ApiRequestError) {
        showToast(err.error.message || 'Could not resend OTP. Please try again.')
      } else {
        showToast('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerifyOtp = async (event) => {
    event.preventDefault()
    const otp = code.join('')
    const parsed = otpVerifySchema.safeParse({ mobile, otp })
    const fieldErrors = withCountryMobileRule(parsed.success ? {} : toFieldErrors(parsed.error.issues))
    if (Object.keys(fieldErrors).length) return setErrors(fieldErrors)

    setErrors({})
    setSubmitting(true)
    try {
      const user = await loginWithOtp(mobileNo, otp)
      showToast(`Welcome back, ${user.name || 'seeker'}!`)
      // AuthProvider flips status to "authenticated"; LoginPage's effect follows the redirect.
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setErrors(toFieldErrors(err.error.details))
        showToast(err.error.message || 'Invalid OTP. Please try again.')
      } else {
        showToast('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1)
    setCode((prev) => {
      const next = [...prev]
      next[index] = digit
      return next
    })
    if (digit && index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus()
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    event.preventDefault()
    setCode((prev) => {
      const next = [...prev]
      for (let i = 0; i < OTP_LENGTH; i += 1) next[i] = pasted[i] ?? next[i]
      return next
    })
    inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
  }

  return (
    <div id="view-otp-frame" className="auth-view-panel">
      <div className="space-y-2 mb-8 text-center sm:text-left">
        <h2 className="text-3xl font-royal tracking-wide text-astroDark uppercase font-medium">WhatsApp Sign In</h2>
        <p className="text-[11px] uppercase font-bold tracking-[0.25em] text-amber-800/60">
          {step === 'mobile' ? 'Instant OTP Verification' : 'Enter the Code We Sent'}
        </p>
        <p className="text-sm text-stone-500 pt-1">
          <button type="button" onClick={onBackToLogin} className="text-goldAccent font-semibold hover:underline outline-none focus:underline">← Back to Password Login</button>
        </p>
      </div>

      {step === 'mobile' ? (
        <form onSubmit={handleSendOtp} className="space-y-5" noValidate>
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-2">
              <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
              WhatsApp Number
            </label>
            <div className="relative flex items-center">
              <CountryCodeSelect
                inputId="otp-country-code"
                value={countryIso2}
                onChange={handleCountryChange}
              />
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                required
                placeholder={phonePlaceholder}
                className="vault-input-phone has-country"
                aria-invalid={Boolean(errors.mobile)}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, maxDigits))}
              />
            </div>
            {errors.mobile && <p className="text-xs text-red-600 mt-1.5 tracking-wide">{errors.mobile}</p>}
          </div>

          <button type="submit" disabled={submitting} className="shine-btn w-full bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
            {submitting ? 'Sending OTP…' : 'Send OTP via WhatsApp'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="space-y-5" noValidate>
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>Code sent to <span className="font-semibold text-astroDark">{country.dialCode} {mobile}</span></span>
            <button type="button" onClick={() => { setStep('mobile'); setErrors({}) }} className="text-goldAccent font-semibold hover:underline outline-none focus:underline">Change</button>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-3">Enter OTP</label>
            <div className="flex justify-between gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputsRef.current[index] = el }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className="otp-box"
                  aria-invalid={Boolean(errors.otp)}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                />
              ))}
            </div>
            {errors.otp && <p className="text-xs text-red-600 mt-1.5 tracking-wide">{errors.otp}</p>}
          </div>

          <button type="submit" disabled={submitting} className="shine-btn w-full bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
            {submitting ? 'Verifying…' : 'Verify & Sign In →'}
          </button>

          <p className="text-center text-sm text-stone-500">
            {resendCooldown > 0 ? (
              <>Resend OTP in <span className="font-semibold text-astroDark">{resendCooldown}s</span></>
            ) : (
              <button type="button" onClick={handleResendOtp} disabled={submitting} className="text-goldAccent font-semibold hover:underline outline-none focus:underline disabled:opacity-60 disabled:no-underline">Resend OTP</button>
            )}
          </p>
        </form>
      )}
    </div>
  )
}
